import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type ApiError, type RouteAnomalyAlertView, type RouteAnomalyCheckInView, type RouteAnomalyEventView, type RouteAnomalyResolutionCodeView, type RouteAnomalyStatusView } from '@vello/api-client';
import { formatDistance } from '@vello/format';
import {
  AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, FieldCell, KeyValueList, ListTable, Notice, PageHeader, Panel, Select,
  SectionTitle, Cluster, Stack, Text, Timeline, type Column, type TimelineEntry,
} from '@vello/web-theme';
import {
  DISMISS_CODES,
  RESOLVE_CODES,
  ageParts,
  anomalyActions,
  anomalyEvidence,
  anomalyPolicyOrigin,
  anomalyTone,
  formatMarketInstant,
  marketTimeZone,
  routeAnomalyAuthority,
  tailOf,
} from '../../src/index';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { PersonName } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { usePlaceNames } from '../i18n/scope-name';

/**
 * Operações › Anomalias de rota (W9-OPS-LIVE-MAP, D471, ADR-0089; X-ray A-29): the alerts the worker raises for a ride under
 * way that left its route, stopped for long or stopped alone at night. The console lists a market's alerts (open and
 * acknowledged by default); a row opens the alert's DRAWER (the Directory pattern): why it was raised in words (the measured
 * values against the policy thresholds, the policy and model in force), where and when, the ride and the people by name, and
 * its history (who acknowledged, reopened, resolved or dismissed it, when, why).
 *
 * The actions live in the drawer footer, for an operator with `safety:manage`. Acknowledging is immediate and can be taken back
 * (the toast's undo, or "Reabrir": ACKNOWLEDGED → OPEN, audited with the operator); resolving and dismissing ask for a reason
 * of the CLOSED vocabulary (the confirm dialog's chips) and an optional note, and a closed alert is frozen. The actor is the
 * session; a concurrent change answers `REVISION_CONFLICT` and the screen reloads instead of overwriting.
 *
 * Neutral by construction: no country, language or zone is named; every alert carries its reason code and the policy and
 * model version that produced it. Read only for a viewer without `safety:manage`.
 */
const OPEN_STATUSES: readonly RouteAnomalyStatusView[] = ['OPEN', 'ACKNOWLEDGED'];
type StatusFilter = 'open' | 'all';
type Action = 'acknowledge' | 'reopen' | 'resolve' | 'dismiss';

/** "Why": the numbers behind the alert as a sentence + the rows, so a reader sees what tripped it and where the line is. */
function WhyPanel({ alert }: { readonly alert: RouteAnomalyAlertView }): ReactElement {
  const { t, lang } = useT();
  const e = anomalyEvidence(alert);
  const ctx = { locale: lang, timeZone: 'UTC', measurement: 'metric' } as const;
  const percent = (bp: number): string => new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 0 }).format(bp / 10_000);
  const duration = (seconds: number): string => {
    const p = ageParts(seconds);
    return p === undefined ? '—' : t(`ranom.dur.${p.unit}`, { value: p.value });
  };
  const value = (n: number): string => (e.kind === 'deviation' ? percent(n) : duration(n));
  const measured = value(e.measured);
  const threshold = e.threshold !== undefined ? value(e.threshold) : undefined;
  const sentence =
    e.kind === 'deviation'
      ? t(threshold !== undefined ? 'ranom.why.deviation' : 'ranom.why.deviationNoLimit', { measured, threshold: threshold ?? '' })
      : t(e.night ? (threshold !== undefined ? 'ranom.why.nightStop' : 'ranom.why.nightStopNoLimit') : threshold !== undefined ? 'ranom.why.stop' : 'ranom.why.stopNoLimit', { measured, threshold: threshold ?? '' });
  return (
    <Stack gap={3}>
      <Text as="p">{sentence}</Text>
      <KeyValueList
        items={[
          ...(e.kind === 'deviation'
            ? [
                { label: t('ranom.why.expected'), value: formatDistance(e.expectedMeters, ctx), mono: true },
                { label: t('ranom.why.projected'), value: formatDistance(e.projectedMeters, ctx), mono: true },
                { label: t('ranom.why.measuredDeviation'), value: measured, mono: true },
              ]
            : [{ label: t('ranom.why.measuredStop'), value: measured, mono: true }, ...(e.night ? [{ label: t('ranom.why.condition'), value: t('ranom.why.conditionNight') }] : [])]),
          { label: t('ranom.why.limit'), value: threshold ?? t('ranom.why.limitUnknown'), mono: threshold !== undefined },
        ]}
      />
    </Stack>
  );
}

export function RouteAnomaliesScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { navigate } = useRouter();
  const { toast, confirm } = useConsole();
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const countryName = useCountryName();
  const place = usePlaceNames();
  const authority = routeAnomalyAuthority(grants);

  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [filter, setFilter] = useState<StatusFilter>('open');
  const [rows, setRows] = useState<readonly RouteAnomalyAlertView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [detail, setDetail] = useState<{ readonly alert: RouteAnomalyAlertView; readonly history: readonly RouteAnomalyEventView[]; readonly checkIn?: RouteAnomalyCheckInView | null } | undefined>(undefined);
  const [rider, setRider] = useState<{ readonly id?: string; readonly code?: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);
  const openSeq = useRef(0);
  const openIdRef = useRef<string | undefined>(undefined);
  openIdRef.current = openId;

  const refuse = useCallback(
    (error: ApiError): string => explain(error) ?? t('lmap.err.generic', { code: fail(error) }),
    [t, fail, explain],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setRows(undefined);
      setError(undefined);
      return;
    }
    const res = await runtime.ops.routeAnomalies(market, filter === 'open' ? OPEN_STATUSES : []);
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setRows(res.value.alerts);
      setError(undefined);
    } else {
      setRows((prev) => prev ?? []);
      setError(refuse(res.error));
    }
  }, [runtime, market, filter, refuse]);

  useEffect(() => {
    setRows(undefined);
    setOpenId(undefined);
    void load();
  }, [load]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== undefined });

  /** The drawer's own read: the alert with its history (a closed alert that left the list stays readable). */
  const loadDetail = useCallback(
    async (alertId: string): Promise<void> => {
      if (market === undefined) return;
      const res = await runtime.ops.routeAnomaly(market, alertId);
      if (res.ok && alertId === openIdRef.current) setDetail({ alert: res.value.alert, history: res.value.history, checkIn: res.value.checkIn });
    },
    [runtime, market],
  );

  const openAlert = (a: RouteAnomalyAlertView): void => {
    const seq = ++openSeq.current;
    openIdRef.current = a.alertId;
    setOpenId(a.alertId);
    setDetail({ alert: a, history: [] });
    setRider(undefined);
    void loadDetail(a.alertId);
    // The rider is not on the alert: the ride's support card names it (and the ride's code), when the operator may read it.
    void runtime.rideSupport.detail(a.rideId).then((res) => {
      if (seq !== openSeq.current) return;
      setRider(res.ok ? { ...(res.value.rider?.id !== undefined ? { id: res.value.rider.id } : {}), code: res.value.ride.code } : {});
    });
  };

  const close = (): void => {
    openSeq.current += 1;
    openIdRef.current = undefined;
    setOpenId(undefined);
  };

  const shown = detail !== undefined && detail.alert.alertId === openId ? detail.alert : undefined;

  /** One action on the shown alert. The server pins the lifecycle; a refusal (or a conflict) is said and the screen reloads. */
  const perform = async (alert: RouteAnomalyAlertView, action: Action, extra: { readonly code?: RouteAnomalyResolutionCodeView; readonly note?: string } = {}): Promise<RouteAnomalyAlertView | undefined> => {
    if (market === undefined) return undefined;
    setBusy(true);
    const res = await runtime.ops.actOnRouteAnomaly(market, alert.alertId, { action, ...(extra.code !== undefined ? { code: extra.code } : {}), ...(extra.note !== undefined ? { note: extra.note } : {}), revision: alert.revision });
    setBusy(false);
    if (!res.ok) toast({ message: refuse(res.error), icon: 'shieldAlert', tone: 'danger' });
    else if (alert.alertId === openIdRef.current) setDetail((d) => ({ alert: res.value.alert, history: d?.history ?? [], ...(d?.checkIn !== undefined ? { checkIn: d.checkIn } : {}) }));
    await Promise.all([load(), loadDetail(alert.alertId)]);
    return res.ok ? res.value.alert : undefined;
  };

  /** Reconhecer acts at once and offers the way back: the toast's undo reopens the alert (ACKNOWLEDGED → OPEN). */
  const acknowledge = async (alert: RouteAnomalyAlertView): Promise<void> => {
    const done = await perform(alert, 'acknowledge');
    if (done === undefined) return;
    toast({
      message: t('ranom.done.acknowledge'),
      icon: 'check',
      tone: 'success',
      onUndo: () => {
        void perform(done, 'reopen', { note: t('ranom.undoReason') }).then((back) => {
          if (back !== undefined) toast({ message: t('ranom.toast.undone'), icon: 'undo', tone: 'neutral' });
        });
      },
    });
  };

  const reopen = async (alert: RouteAnomalyAlertView): Promise<void> => {
    const reasons = [t('ranom.reopen.reason.mistake'), t('ranom.reopen.reason.followUp')];
    const r = await confirm({
      title: t('ranom.reopen.title'),
      body: t('ranom.reopen.body'),
      confirmLabel: t('ranom.action.reopen'),
      cancelLabel: t('ui.cancel'),
      icon: 'rotateCcw',
      reasons,
      reasonLabel: t('ui.reason'),
      notePlaceholder: t('ranom.closing.note'),
    });
    if (!r.confirmed) return;
    const note = [r.reason, r.note].filter((x): x is string => x !== undefined && x.trim() !== '').join(' — ');
    const back = await perform(alert, 'reopen', note !== '' ? { note } : {});
    if (back !== undefined) toast({ message: t('ranom.done.reopen'), icon: 'undo', tone: 'neutral' });
  };

  /** Resolver / Descartar: a reason of the closed vocabulary is mandatory (chips), the note is optional and internal. */
  const closeAlert = async (alert: RouteAnomalyAlertView, kind: 'resolve' | 'dismiss'): Promise<void> => {
    const codes = kind === 'resolve' ? RESOLVE_CODES : DISMISS_CODES;
    const labels = codes.map((c) => v('safety.route_anomaly_resolution', c));
    const r = await confirm({
      title: t(kind === 'resolve' ? 'ranom.closing.resolveTitle' : 'ranom.closing.dismissTitle', { id: rider?.code ?? tailOf(alert.rideId) }),
      body: t(kind === 'resolve' ? 'ranom.closing.resolveBody' : 'ranom.closing.dismissBody'),
      confirmLabel: t(kind === 'resolve' ? 'ranom.action.resolve' : 'ranom.action.dismiss'),
      cancelLabel: t('ui.cancel'),
      icon: kind === 'resolve' ? 'check' : 'x',
      reasons: labels,
      reasonLabel: t('ranom.closing.code'),
      notePlaceholder: t('ranom.closing.note'),
    });
    if (!r.confirmed || r.reason === undefined) return;
    const code = codes[labels.indexOf(r.reason)];
    if (code === undefined) return;
    const done = await perform(alert, kind, { code, ...(r.note !== undefined && r.note.trim() !== '' ? { note: r.note } : {}) });
    if (done !== undefined) toast({ message: t(`ranom.done.${kind}`), icon: 'check', tone: 'success' });
  };

  const percent = (bp: number): string => new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 0 }).format(bp / 10_000);
  const duration = (seconds: number): string => {
    const p = ageParts(seconds);
    return p === undefined ? '—' : t(`ranom.dur.${p.unit}`, { value: p.value });
  };
  const when = (instant: string | undefined): string => formatMarketInstant(instant, timeZone, lang);

  const columns: readonly Column<RouteAnomalyAlertView>[] = [
    { key: 'when', header: t('ranom.col.detected'), width: 'minmax(150px, 1fr)', render: (a) => <Text role="caption" tone="secondary" mono>{when(a.detectedAt)}</Text> },
    { key: 'trigger', header: t('ranom.col.trigger'), width: 'minmax(170px, 1.2fr)', render: (a) => <strong>{v('safety.trigger', a.trigger)}</strong> },
    { key: 'severity', header: t('ranom.col.severity'), width: 'minmax(100px, 0.7fr)', render: (a) => <Badge tone={anomalyTone(a.severity)}>{v('safety.risk_severity', a.severity)}</Badge> },
    {
      key: 'detail',
      header: t('ranom.col.detail'),
      width: 'minmax(170px, 1.2fr)',
      render: (a) => (
        <Text role="caption" tone="secondary" mono>
          {a.trigger === 'route_anomaly' ? t('ranom.detail.deviation', { value: percent(a.deviationBasisPoints) }) : t('ranom.detail.stopped', { value: duration(a.stoppedSeconds) })}
        </Text>
      ),
    },
    { key: 'ride', header: t('lmap.col.ride'), width: 'minmax(120px, 0.9fr)', render: (a) => <IdRef value={a.rideId} /> },
    { key: 'status', header: t('lmap.col.status'), width: 'minmax(120px, 0.8fr)', render: (a) => <Badge tone={a.status === 'OPEN' ? 'warning' : 'neutral'}>{v('safety.route_anomaly_status', a.status)}</Badge> },
  ];

  /** The policy in words: the scope it was set at and its version, never the stored key. */
  const policyWords = (a: RouteAnomalyAlertView): string => {
    const o = anomalyPolicyOrigin(a.policyVersion);
    if (o === undefined) return t('ranom.policy.unknown');
    if (o.kind === 'default') return t('ranom.policy.default');
    if (o.kind === 'invalid') return t('ranom.policy.invalid');
    if (o.kind === 'country') return t('ranom.policy.country', { country: countryName(o.ref), version: o.version });
    if (o.kind === 'region') return t('ranom.policy.region', { region: place.region(o.ref), version: o.version });
    return t('ranom.policy.market', { market: markets.find((m) => m.id === o.ref)?.name ?? o.ref, version: o.version });
  };
  const modelWords = (a: RouteAnomalyAlertView): string | undefined => {
    const n = /-v(\d+)$/.exec(a.modelVersion)?.[1];
    return n === undefined ? undefined : t('ranom.model.words', { version: n });
  };

  const historyEntries = (a: RouteAnomalyAlertView, history: readonly RouteAnomalyEventView[]): readonly TimelineEntry[] => {
    const events: TimelineEntry[] = history.map((e, i) => {
      const reason = e.code !== undefined && e.kind !== 'REOPENED' ? v('safety.route_anomaly_resolution', e.code) : undefined;
      const detailLine = [reason, e.note].filter((x): x is string => x !== undefined && x !== '').join(' — ');
      return {
        id: `e${i}`,
        tone: e.kind === 'DISMISSED' ? 'warning' : 'neutral',
        title: v('safety.route_anomaly_event', e.kind),
        ...(detailLine !== '' ? { detail: detailLine } : {}),
        meta: <>{when(e.at)} · <PersonName id={e.by} /></>,
      };
    });
    // Newest first, like the directory's trail; the detection is the oldest entry.
    return [...events.reverse(), { id: 'detected', tone: a.severity === 'high' ? 'danger' : 'warning', title: t('ranom.history.detected'), meta: <>{when(a.detectedAt)}</> }];
  };

  const actions = shown !== undefined ? anomalyActions(shown, authority.mayManage) : [];

  return (
    <>
      <PageHeader
        title={t('ranom.title')}
        subtitle={t('ranom.subtitle')}
      />

      {!authority.mayManage && (
        <Notice tone="neutral">{t('ranom.readOnly')}</Notice>
      )}

      <Panel>
        <Stack gap={4}>
          <Cluster gap={4} wrap>
            <FieldCell size="sm">
              <Field label={t('ranom.filter.status')} htmlFor="ranom-status">
                <Select id="ranom-status" value={filter} onChange={(e) => setFilter(e.target.value === 'all' ? 'all' : 'open')}>
                  <option value="open">{t('ranom.filter.open')}</option>
                  <option value="all">{t('ranom.filter.all')}</option>
                </Select>
              </Field>
            </FieldCell>
          </Cluster>
        </Stack>
      </Panel>

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <ListTable
          columns={columns}
          rows={rows ?? []}
          rowKey={(a) => a.alertId}
          onRowClick={openAlert}
          {...(openId !== undefined ? { selectedKey: openId } : {})}
          loading={rows === undefined}
          loadingLabel={t('ui.loading')}
          error={error}
          onRetry={() => void load()}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={t('ranom.empty.title')} detail={t('ranom.empty.detail')} />}
          minWidth={900}
        />
      )}

      <Drawer open={shown !== undefined} onClose={close} labelledBy="ranom-drawer-title">
        {shown !== undefined && detail !== undefined && (
          <>
            <DrawerHeader
              titleId="ranom-drawer-title"
              title={v('safety.trigger', shown.trigger)}
              subtitle={<Badge tone={anomalyTone(shown.severity)}>{v('safety.risk_severity', shown.severity)}</Badge>}
              actions={<Badge tone={shown.status === 'OPEN' ? 'warning' : 'neutral'}>{v('safety.route_anomaly_status', shown.status)}</Badge>}
              onClose={close}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
                <section>
                  <SectionTitle level={3} size="sub">{t('ranom.section.why')}</SectionTitle>
                  <WhyPanel alert={shown} />
                </section>
                <section>
                  <SectionTitle level={3} size="sub">{t('ranom.section.whereWhen')}</SectionTitle>
                  <KeyValueList
                    items={[
                      { label: t('ranom.col.detected'), value: when(shown.detectedAt), mono: true },
                      { label: t('ranom.field.lastEvaluated'), value: when(shown.lastEvaluatedAt), mono: true },
                      {
                        label: t('ranom.field.position'),
                        value: shown.lastPosition !== undefined ? `${shown.lastPosition.latitude.toFixed(5)}, ${shown.lastPosition.longitude.toFixed(5)}` : t('ranom.field.noPosition'),
                        mono: shown.lastPosition !== undefined,
                      },
                    ]}
                  />
                </section>
                <section>
                  <SectionTitle level={3} size="sub">{t('ranom.section.people')}</SectionTitle>
                  <KeyValueList
                    items={[
                      {
                        label: t('lmap.col.ride'),
                        value: (
                          <Cluster gap={2} wrap>
                            <Button size="sm" variant="ghost" icon="arrowRight" onClick={() => navigate('ride_detail', { id: shown.rideId })}>{t('ranom.openRide')}</Button>
                            <IdRef value={shown.rideId} copy full />
                          </Cluster>
                        ),
                      },
                      { label: t('ranom.field.driver'), value: <PersonName id={shown.driverId} /> },
                      { label: t('ranom.field.rider'), value: rider === undefined ? t('ui.loading') : <PersonName id={rider.id} /> },
                    ]}
                  />
                </section>
                {detail.checkIn !== undefined && detail.checkIn !== null && (
                  <section>
                    <SectionTitle level={3} size="sub">{t('ranom.section.checkIn')}</SectionTitle>
                    <KeyValueList
                      items={[
                        { label: t('ranom.checkIn.status'), value: v('safety.check_in_status', detail.checkIn.status) },
                        { label: t('ranom.field.rider'), value: detail.checkIn.riderAnswer === undefined ? t('ranom.checkIn.answer.none') : v('safety.check_in_answer', detail.checkIn.riderAnswer) },
                        { label: t('ranom.field.driver'), value: detail.checkIn.driverAnswer === undefined ? t('ranom.checkIn.answer.none') : v('safety.check_in_answer', detail.checkIn.driverAnswer) },
                        { label: t('ranom.checkIn.askedAt'), value: when(detail.checkIn.promptedAt) },
                        ...(detail.checkIn.escalationIncidentId !== undefined ? [{ label: t('ranom.checkIn.escalation'), value: <IdRef value={detail.checkIn.escalationIncidentId} copy /> }] : []),
                      ]}
                    />
                  </section>
                )}
                <section>
                  <SectionTitle level={3} size="sub">{t('ranom.section.policy')}</SectionTitle>
                  <KeyValueList
                    items={[
                      { label: t('ranom.field.policy'), value: policyWords(shown) },
                      ...(modelWords(shown) !== undefined ? [{ label: t('ranom.field.model'), value: modelWords(shown) as string }] : []),
                    ]}
                  />
                </section>
                <section>
                  <SectionTitle level={3} size="sub">{t('ranom.section.history')}</SectionTitle>
                  <Timeline entries={historyEntries(shown, detail.history)} />
                </section>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              {actions.includes('acknowledge') && <Button variant="primary" disabled={busy} onClick={() => void acknowledge(shown)}>{t('ranom.action.acknowledge')}</Button>}
              {actions.includes('reopen') && <Button variant="secondary" disabled={busy} icon="rotateCcw" onClick={() => void reopen(shown)}>{t('ranom.action.reopen')}</Button>}
              {actions.includes('resolve') && <Button variant="secondary" disabled={busy} onClick={() => void closeAlert(shown, 'resolve')}>{t('ranom.action.resolve')}</Button>}
              {actions.includes('dismiss') && <Button variant="ghost" disabled={busy} onClick={() => void closeAlert(shown, 'dismiss')}>{t('ranom.action.dismiss')}</Button>}
              {actions.length === 0 && <Button variant="ghost" onClick={close}>{t('ui.close')}</Button>}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
